import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Stack,
  IconButton,
  useTheme
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import EditIcon from '@mui/icons-material/Edit';
import { downloadSkiTemplate, parseCSV, validateAllSkis, validateSkiData as validateSki } from '../utils/skiSpreadsheet';
import { bulkAddSkis } from '../api';

// Export a version that accepts custom bulk add function
export function SkiSpreadsheetUploadWithCustomBulk({ open, onClose, onSuccess, bulkAddFunction }) {
  const actualBulkAdd = bulkAddFunction || bulkAddSkis;
  return <SkiSpreadsheetUpload open={open} onClose={onClose} onSuccess={onSuccess} bulkAddFunction={actualBulkAdd} />;
}

const BRANDS = ['Fischer', 'Salomon', 'Atomic', 'Rossignol', 'Madshus'];
const TYPES = ['classic', 'skate'];
const ROSSI_MOLDS = ['S1', 'S2', 'S3', 'SX', 'C1', 'C2', 'C3', 'Zero', 'skin', 'Double Pole'];
const ROSSI_STIFFNESS = ['Soft', 'Medium', 'Stiff'];

// Helper function to pad ski number with leading zeros to 3 digits
const padSkiNumber = (skiNumber) => {
  if (!skiNumber) return skiNumber;
  const numStr = String(skiNumber).trim();
  // Only pad if it's a numeric string
  if (/^\d+$/.test(numStr)) {
    return numStr.padStart(3, '0');
  }
  return numStr; // Return as-is if not purely numeric
};

export default function SkiSpreadsheetUpload({ open, onClose, onSuccess, bulkAddFunction }) {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState(null);
  const [validatedSkis, setValidatedSkis] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitResult, setSubmitResult] = useState(null);

  const steps = ['Download Template', 'Upload Spreadsheet', 'Validate & Edit', 'Submit'];

  const handleDownloadTemplate = () => {
    downloadSkiTemplate();
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFile(file);
    setError('');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const parsed = parseCSV(csvText);
        
        if (parsed.length === 0) {
          setError('No valid ski data found in the file. Please check the format.');
          return;
        }

        // Pad ski numbers with leading zeros to 3 digits
        const parsedWithPaddedNumbers = parsed.map(ski => ({
          ...ski,
          ski_number: padSkiNumber(ski.ski_number)
        }));
        
        const validated = validateAllSkis(parsedWithPaddedNumbers);
        setValidatedSkis(validated);
        setActiveStep(2);
      } catch (err) {
        setError('Error parsing CSV: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  const handleEdit = (index) => {
    setEditingRow(index);
  };

  const handleSaveEdit = (index) => {
    const updated = [...validatedSkis];
    // Ensure ski_number is padded before saving
    if (updated[index].ski_number) {
      updated[index].ski_number = padSkiNumber(updated[index].ski_number);
    }
    const validation = validateSki(updated[index], index + 1);
    updated[index] = {
      ...updated[index],
      validation
    };
    setValidatedSkis(updated);
    setEditingRow(null);
  };

  const handleFieldChange = (index, field, value) => {
    const updated = [...validatedSkis];
    // Pad ski_number with leading zeros when changed
    if (field === 'ski_number') {
      updated[index] = {
        ...updated[index],
        [field]: padSkiNumber(value)
      };
    } else {
      updated[index] = {
        ...updated[index],
        [field]: value
      };
    }
    setValidatedSkis(updated);
  };


  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    
    // Filter out invalid skis
    const validSkis = validatedSkis
      .filter(ski => ski.validation.isValid)
      .map(({ rowIndex, validation, ...ski }) => ({
        ski_number: ski.ski_number,
        serial_number: ski.serial_number || null,
        brand: ski.brand,
        type: ski.type,
        fa: ski.fa ?? null,
        hr: ski.hr ?? null,
        mf: ski.mf ?? null,
        h4: ski.h4 ?? null,
        rossi_flex: ski.rossi_flex ?? null,
        rossi_height: ski.rossi_height ?? null,
        rossi_stiffness: ski.rossi_stiffness || null,
        mold: ski.mold || null,
        grind: ski.grind || null,
        base: ski.base || null,
        ski_length_cm: ski.ski_length_cm || null,
        year_made: ski.year_made || null,
        temperature_color: ski.temperature_color ?? null,
        ski_category: ski.ski_category || null
      }));

    if (validSkis.length === 0) {
      setError('No valid skis to upload. Please fix the errors first.');
      setLoading(false);
      return;
    }

    try {
      const bulkAdd = bulkAddFunction || bulkAddSkis;
      const result = await bulkAdd(validSkis);
      setSubmitResult(result);
      setActiveStep(3);
      if (onSuccess) {
        onSuccess(result);
      }
    } catch (err) {
      setError(err.message || 'Failed to upload skis');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setFile(null);
    setValidatedSkis([]);
    setEditingRow(null);
    setError('');
    setSubmitResult(null);
  };

  const validCount = validatedSkis.filter(s => s.validation.isValid).length;
  const invalidCount = validatedSkis.length - validCount;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Upload Skis from Spreadsheet</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Step 1: Download Template */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Step 1: Download Template</Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Download the CSV template file. The template includes example rows showing the correct format. Fill it out with your ski data, then upload it in the next step.
            </Alert>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              size="large"
            >
              Download Template
            </Button>
            
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Field Instructions</Typography>
              
              <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.1)', border: `1px solid ${theme.palette.error.main}` }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Required Fields (must be filled):</Typography>
                <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
                  • <strong>ski_number:</strong> Unique identifier for the ski (e.g., "1", "A1", "Race-1")
                </Typography>
                <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
                  • <strong>brand:</strong> Must be one of: {BRANDS.join(', ')}
                </Typography>
                <Typography variant="body2" component="div">
                  • <strong>type:</strong> Must be one of: {TYPES.join(', ')}
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)', border: `1px solid ${theme.palette.info.main}` }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Optional Fields (can be left empty):</Typography>
                <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
                  • <strong>serial_number:</strong> Serial number from manufacturer (text)
                </Typography>
                <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
                  • <strong>grind:</strong> Grind information (text)
                </Typography>
                <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
                  • <strong>base:</strong> Base material/type (text)
                </Typography>
                <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
                  • <strong>mold:</strong> Mold information (text)
                </Typography>
                <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
                  • <strong>ski_length_cm:</strong> Length in centimeters (number)
                </Typography>
                <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
                  • <strong>year_made:</strong> Year the ski was manufactured (number, e.g., 2023)
                </Typography>
                <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
                  • <strong>temperature_color:</strong> Temperature preference (number, 0-100, where 0=cold/blue, 100=warm/red)
                </Typography>
                <Typography variant="body2" component="div">
                  • <strong>ski_category:</strong> Category of ski (text: "race", "retired_race", or "training")
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(237, 108, 2, 0.2)' : 'rgba(237, 108, 2, 0.1)', border: `1px solid ${theme.palette.warning.main}` }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Brand-Specific Fields (only fill for that brand):</Typography>
                
                <Typography variant="body2" component="div" sx={{ mb: 1, mt: 1 }}>
                  <strong>FISCHER:</strong>
                </Typography>
                <Typography variant="body2" component="div" sx={{ ml: 2, mb: 0.5 }}>
                  • <strong>fa:</strong> Flex value (number, e.g., 45)
                </Typography>
                <Typography variant="body2" component="div" sx={{ ml: 2, mb: 1 }}>
                  • <strong>hr:</strong> Camber height (number, e.g., 2.5)
                </Typography>

                <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                  <strong>SALOMON / ATOMIC:</strong>
                </Typography>
                <Typography variant="body2" component="div" sx={{ ml: 2, mb: 0.5 }}>
                  • <strong>mf:</strong> Flex value (number, e.g., 50)
                </Typography>
                <Typography variant="body2" component="div" sx={{ ml: 2, mb: 1 }}>
                  • <strong>h4:</strong> Camber height (number, e.g., 3.0)
                </Typography>

                <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                  <strong>ROSSIGNOL:</strong>
                </Typography>
                <Typography variant="body2" component="div" sx={{ ml: 2, mb: 0.5 }}>
                  • <strong>rossi_flex:</strong> Flex value (number, e.g., 45)
                </Typography>
                <Typography variant="body2" component="div" sx={{ ml: 2, mb: 0.5 }}>
                  • <strong>rossi_height:</strong> Camber height (number, e.g., 2.5)
                </Typography>
                <Typography variant="body2" component="div" sx={{ ml: 2, mb: 0.5 }}>
                  • <strong>rossi_stiffness:</strong> Must be one of: {ROSSI_STIFFNESS.join(', ')}
                </Typography>
                <Typography variant="body2" component="div" sx={{ ml: 2, mb: 1 }}>
                  • <strong>mold:</strong> Must be one of: {ROSSI_MOLDS.join(', ')}
                </Typography>
              </Paper>

              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Tip:</strong> The downloaded template includes example rows showing how to format data for Fischer, Salomon, and Rossignol skis. You can use these as a reference or delete them before adding your own data.
                </Typography>
              </Alert>
            </Box>
          </Box>
        )}

        {/* Step 2: Upload File */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Step 2: Upload Spreadsheet</Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Select the CSV file you filled out with your ski data. The file will be automatically validated after upload.
            </Alert>
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-upload"
              type="file"
              onChange={handleFileUpload}
              key={file ? file.name : 'no-file'} // Reset input when file changes
            />
            <label htmlFor="csv-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={<UploadFileIcon />}
                size="large"
              >
                Choose CSV File
              </Button>
            </label>
            {file && (
              <Alert severity="success" sx={{ mt: 2 }}>
                File selected: {file.name} ({validatedSkis.length} ski(s) found)
              </Alert>
            )}
          </Box>
        )}

        {/* Step 3: Validate & Edit */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Step 3: Validate & Edit ({validCount} valid, {invalidCount} need fixing)
            </Typography>
            <Alert severity={invalidCount === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {invalidCount === 0
                ? 'All skis are valid and ready to upload!'
                : `Please fix ${invalidCount} ski(s) with errors before submitting.`}
            </Alert>
            
            <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Ski #</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Errors</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validatedSkis.map((ski, idx) => (
                    <React.Fragment key={idx}>
                      <TableRow sx={{ bgcolor: ski.validation.isValid ? 'success.light' : 'error.light' }}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{ski.ski_number || '-'}</TableCell>
                        <TableCell>{ski.brand || '-'}</TableCell>
                        <TableCell>{ski.type || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            icon={ski.validation.isValid ? <CheckCircleIcon /> : <ErrorIcon />}
                            label={ski.validation.isValid ? 'Valid' : 'Invalid'}
                            color={ski.validation.isValid ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {ski.validation.errors.length > 0 && (
                            <Typography variant="caption" color="error">
                              {ski.validation.errors.join(', ')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleEdit(idx)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      {editingRow === idx && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ bgcolor: 'action.selected', p: 3 }}>
                            <Stack spacing={3}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Edit Ski Row {idx + 1}
                              </Typography>
                              
                              {/* Required Fields Section */}
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block', color: 'text.secondary' }}>
                                  Required Fields
                                </Typography>
                                <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 2 }}>
                                  <TextField
                                    label="Ski Number *"
                                    value={ski.ski_number || ''}
                                    onChange={(e) => handleFieldChange(idx, 'ski_number', e.target.value)}
                                    size="small"
                                    required
                                    error={!ski.ski_number}
                                    sx={{ minWidth: 150 }}
                                  />
                                  <TextField
                                    select
                                    label="Brand *"
                                    value={ski.brand || ''}
                                    onChange={(e) => handleFieldChange(idx, 'brand', e.target.value)}
                                    size="small"
                                    required
                                    error={!ski.brand}
                                    sx={{ minWidth: 150 }}
                                  >
                                    {BRANDS.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                                  </TextField>
                                  <TextField
                                    select
                                    label="Type *"
                                    value={ski.type || ''}
                                    onChange={(e) => handleFieldChange(idx, 'type', e.target.value)}
                                    size="small"
                                    required
                                    error={!ski.type}
                                    sx={{ minWidth: 150 }}
                                  >
                                    {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                  </TextField>
                                </Stack>
                              </Box>

                              {/* Brand-Specific Fields */}
                              {(ski.brand === 'Fischer' || ['Salomon', 'Atomic'].includes(ski.brand) || ski.brand === 'Rossignol') && (
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block', color: 'text.secondary' }}>
                                    Brand-Specific Fields
                                  </Typography>
                                  <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 2 }}>
                                    {ski.brand === 'Fischer' && (
                                      <>
                                        <TextField
                                          label="FA"
                                          type="number"
                                          value={ski.fa || ''}
                                          onChange={(e) => handleFieldChange(idx, 'fa', e.target.value)}
                                          size="small"
                                          sx={{ minWidth: 120 }}
                                          error={ski.fa && isNaN(parseFloat(ski.fa))}
                                        />
                                        <TextField
                                          label="HR"
                                          type="number"
                                          value={ski.hr || ''}
                                          onChange={(e) => handleFieldChange(idx, 'hr', e.target.value)}
                                          size="small"
                                          sx={{ minWidth: 120 }}
                                          error={ski.hr && isNaN(parseFloat(ski.hr))}
                                        />
                                      </>
                                    )}
                                    {['Salomon', 'Atomic'].includes(ski.brand) && (
                                      <>
                                        <TextField
                                          label="MF"
                                          type="number"
                                          value={ski.mf || ''}
                                          onChange={(e) => handleFieldChange(idx, 'mf', e.target.value)}
                                          size="small"
                                          sx={{ minWidth: 120 }}
                                          error={ski.mf && isNaN(parseFloat(ski.mf))}
                                        />
                                        <TextField
                                          label="H4"
                                          type="number"
                                          value={ski.h4 || ''}
                                          onChange={(e) => handleFieldChange(idx, 'h4', e.target.value)}
                                          size="small"
                                          sx={{ minWidth: 120 }}
                                          error={ski.h4 && isNaN(parseFloat(ski.h4))}
                                        />
                                      </>
                                    )}
                                    {ski.brand === 'Rossignol' && (
                                      <>
                                        <TextField
                                          label="Rossi Flex"
                                          type="number"
                                          value={ski.rossi_flex || ''}
                                          onChange={(e) => handleFieldChange(idx, 'rossi_flex', e.target.value)}
                                          size="small"
                                          sx={{ minWidth: 120 }}
                                          error={ski.rossi_flex && isNaN(parseFloat(ski.rossi_flex))}
                                        />
                                        <TextField
                                          label="Rossi Height"
                                          type="number"
                                          value={ski.rossi_height || ''}
                                          onChange={(e) => handleFieldChange(idx, 'rossi_height', e.target.value)}
                                          size="small"
                                          sx={{ minWidth: 120 }}
                                          error={ski.rossi_height && isNaN(parseFloat(ski.rossi_height))}
                                        />
                                        <TextField
                                          select
                                          label="Rossi Stiffness"
                                          value={ski.rossi_stiffness || ''}
                                          onChange={(e) => handleFieldChange(idx, 'rossi_stiffness', e.target.value)}
                                          size="small"
                                          sx={{ minWidth: 150 }}
                                        >
                                          {ROSSI_STIFFNESS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                        </TextField>
                                        <TextField
                                          select
                                          label="Mold (Rossignol)"
                                          value={ski.mold || ''}
                                          onChange={(e) => handleFieldChange(idx, 'mold', e.target.value)}
                                          size="small"
                                          sx={{ minWidth: 150 }}
                                        >
                                          {ROSSI_MOLDS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                        </TextField>
                                      </>
                                    )}
                                  </Stack>
                                </Box>
                              )}

                              {/* Optional Fields Section */}
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block', color: 'text.secondary' }}>
                                  Optional Fields
                                </Typography>
                                <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 2 }}>
                                  <TextField
                                    label="Serial Number"
                                    value={ski.serial_number || ''}
                                    onChange={(e) => handleFieldChange(idx, 'serial_number', e.target.value)}
                                    size="small"
                                    sx={{ minWidth: 150 }}
                                  />
                                  {ski.brand !== 'Rossignol' && (
                                    <TextField
                                      label="Mold"
                                      value={ski.mold || ''}
                                      onChange={(e) => handleFieldChange(idx, 'mold', e.target.value)}
                                      size="small"
                                      sx={{ minWidth: 150 }}
                                    />
                                  )}
                                  <TextField
                                    label="Grind"
                                    value={ski.grind || ''}
                                    onChange={(e) => handleFieldChange(idx, 'grind', e.target.value)}
                                    size="small"
                                    sx={{ minWidth: 150 }}
                                  />
                                  <TextField
                                    label="Base"
                                    value={ski.base || ''}
                                    onChange={(e) => handleFieldChange(idx, 'base', e.target.value)}
                                    size="small"
                                    sx={{ minWidth: 150 }}
                                  />
                                  <TextField
                                    label="Length (cm)"
                                    type="number"
                                    value={ski.ski_length_cm || ''}
                                    onChange={(e) => handleFieldChange(idx, 'ski_length_cm', e.target.value)}
                                    size="small"
                                    sx={{ minWidth: 150 }}
                                  />
                                  <TextField
                                    label="Year Made"
                                    type="number"
                                    value={ski.year_made || ''}
                                    onChange={(e) => handleFieldChange(idx, 'year_made', e.target.value)}
                                    size="small"
                                    sx={{ minWidth: 150 }}
                                  />
                                  <TextField
                                    label="Temperature Color"
                                    type="number"
                                    value={ski.temperature_color || ''}
                                    onChange={(e) => handleFieldChange(idx, 'temperature_color', e.target.value)}
                                    size="small"
                                    sx={{ minWidth: 180 }}
                                    inputProps={{ min: 0, max: 100 }}
                                    helperText="0=cold (blue), 100=warm (red)"
                                    error={ski.temperature_color && (isNaN(parseFloat(ski.temperature_color)) || parseFloat(ski.temperature_color) < 0 || parseFloat(ski.temperature_color) > 100)}
                                  />
                                  <TextField
                                    select
                                    label="Ski Category"
                                    value={ski.ski_category || ''}
                                    onChange={(e) => handleFieldChange(idx, 'ski_category', e.target.value)}
                                    size="small"
                                    sx={{ minWidth: 180 }}
                                  >
                                    <MenuItem value="">None</MenuItem>
                                    <MenuItem value="race">Race</MenuItem>
                                    <MenuItem value="retired_race">Retired Race</MenuItem>
                                    <MenuItem value="training">Training</MenuItem>
                                  </TextField>
                                </Stack>
                              </Box>

                              {/* Action Buttons */}
                              <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
                                <Button
                                  variant="contained"
                                  onClick={() => handleSaveEdit(idx)}
                                >
                                  Save Changes
                                </Button>
                                <Button
                                  onClick={() => setEditingRow(null)}
                                >
                                  Cancel
                                </Button>
                              </Stack>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Step 4: Submit Results */}
        {activeStep === 3 && submitResult && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Upload Complete!</Typography>
            <Alert severity="success" sx={{ mb: 2 }}>
              Successfully uploaded {submitResult.success} ski(s)
            </Alert>
            {submitResult.errorCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {submitResult.errorCount} ski(s) failed to upload
              </Alert>
            )}
            {submitResult.errors && submitResult.errors.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Errors:</Typography>
                {submitResult.errors.map((err, idx) => (
                  <Typography key={idx} variant="body2" color="error">
                    Row {err.row}: {err.error}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {activeStep === 0 && (
          <Button variant="contained" onClick={() => setActiveStep(1)}>
            Next
          </Button>
        )}
        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)}>Back</Button>
            <Button variant="contained" onClick={() => setActiveStep(2)} disabled={!file}>
              Next
            </Button>
          </>
        )}
        {activeStep === 2 && (
          <>
            <Button onClick={() => setActiveStep(1)}>Back</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || invalidCount > 0}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Uploading...' : `Upload ${validCount} Ski(s)`}
            </Button>
          </>
        )}
        {activeStep === 3 && (
          <Button variant="contained" onClick={handleReset}>
            Upload More
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

